// StepBanks v2 — Phase 5 enhanced.
//
// Adds:
//   • account_name field (printed on invoices alongside the IBAN)
//   • currency dropdown (default SAR; SAR / USD / EUR / GBP / AED)
//   • live IBAN validation via GET /api/onboarding/iban-check (mod-97
//     + length). Soft: the user can still proceed with a warning.
//   • primary radio enforces single-primary across all rows
//
// Used by: pages/onboarding/CompanyOnboarding.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { axiosInstance } from '../../../App';
import {
  Wallet, ChevronRight, Plus, Trash2, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const CURRENCIES = ['SAR', 'USD', 'EUR', 'GBP', 'AED', 'KWD', 'BHD'];

function emptyAccount() {
  return {
    bank_name: '',
    account_name: '',
    account_number: '',
    iban: '',
    currency: 'SAR',
    is_primary: true,
  };
}

export default function StepBanks({ initialData, onSubmit, busy }) {
  const [accounts, setAccounts] = useState([emptyAccount()]);
  // ibanState[idx] = {ok, warning, checking}
  const [ibanState, setIbanState] = useState({});
  const debounceRef = useRef({});

  useEffect(() => {
    if (initialData?.accounts?.length) {
      const h = setTimeout(() => setAccounts(initialData.accounts), 0);
      return () => clearTimeout(h);
    }
  }, [initialData]);

  const update = (idx, key, value) => {
    setAccounts((arr) => arr.map((a, i) => {
      if (i !== idx) return a;
      return { ...a, [key]: value };
    }).map((a, i) => {
      // Single-primary enforcement.
      if (key === 'is_primary' && value === true) {
        return { ...a, is_primary: i === idx };
      }
      return a;
    }));

    if (key === 'iban') {
      const v = (value || '').trim();
      setIbanState((s) => ({ ...s, [idx]: { checking: true } }));
      clearTimeout(debounceRef.current[idx]);
      debounceRef.current[idx] = setTimeout(async () => {
        if (!v) {
          setIbanState((s) => ({ ...s, [idx]: { ok: null, warning: null } }));
          return;
        }
        try {
          const { data } = await axiosInstance.get(
            `/onboarding/iban-check?iban=${encodeURIComponent(v)}`,
          );
          setIbanState((s) => ({ ...s, [idx]: data }));
        } catch {
          setIbanState((s) => ({ ...s, [idx]: { ok: false, warning: 'check failed' } }));
        }
      }, 400);
    }
  };

  const add = () => setAccounts((arr) => [
    ...arr,
    { ...emptyAccount(), is_primary: arr.length === 0 },
  ]);
  const remove = (idx) => setAccounts((arr) => arr.length === 1 ? arr : arr.filter((_, i) => i !== idx));

  return (
    <div data-testid="step-banks" className="space-y-5">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700"><Wallet size={18} /></div>
        <div>
          <h2 className="text-lg font-bold text-slate-900">Bank accounts</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Add at least one bank. The &quot;primary&quot; account is printed on invoices and
            used by the cash-flow engine. IBANs are mod-97 validated live.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {accounts.map((a, idx) => {
          const ibs = ibanState[idx] || {};
          return (
            <div
              key={idx}
              data-testid={`bank-row-${idx}`}
              className={`p-4 rounded-lg border ${
                a.is_primary ? 'border-indigo-200 bg-indigo-50/30' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-3">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Bank name</label>
                  <Input
                    data-testid={`bank-name-${idx}`}
                    placeholder="Al Rajhi Bank"
                    value={a.bank_name}
                    onChange={(e) => update(idx, 'bank_name', e.target.value)}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Account name</label>
                  <Input
                    data-testid={`bank-account-name-${idx}`}
                    placeholder="ACME Operations Account"
                    value={a.account_name}
                    onChange={(e) => update(idx, 'account_name', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Currency</label>
                  <select
                    data-testid={`bank-currency-${idx}`}
                    value={a.currency || 'SAR'}
                    onChange={(e) => update(idx, 'currency', e.target.value)}
                    className="w-full px-2 py-2 rounded-md border border-slate-200 bg-white text-sm"
                  >
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Account #</label>
                  <Input
                    data-testid={`bank-acc-${idx}`}
                    placeholder="0000000000"
                    value={a.account_number}
                    onChange={(e) => update(idx, 'account_number', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2 flex items-end justify-end gap-2">
                  <label className="text-[11px] text-slate-600 flex items-center gap-1 cursor-pointer">
                    <input
                      type="radio"
                      name="primary-bank"
                      data-testid={`bank-primary-${idx}`}
                      checked={!!a.is_primary}
                      onChange={() => update(idx, 'is_primary', true)}
                    />
                    Primary
                  </label>
                  {accounts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      className="text-rose-500 hover:text-rose-700 p-1"
                      data-testid={`bank-remove-${idx}`}
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
                <div className="md:col-span-12">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">IBAN</label>
                  <div className="relative">
                    <Input
                      data-testid={`bank-iban-${idx}`}
                      placeholder="SA00 0000 0000 0000 0000 0000"
                      value={a.iban}
                      onChange={(e) => update(idx, 'iban', e.target.value.toUpperCase())}
                      className={
                        ibs.ok === true ? 'border-emerald-400' :
                        ibs.ok === false && a.iban ? 'border-rose-400' : ''
                      }
                    />
                    {a.iban && (
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                        {ibs.checking ? (
                          <span className="text-slate-400">…</span>
                        ) : ibs.ok === true ? (
                          <span className="text-emerald-600 flex items-center gap-1" data-testid={`bank-iban-valid-${idx}`}>
                            <CheckCircle2 size={12} /> Valid
                          </span>
                        ) : ibs.ok === false ? (
                          <span className="text-rose-600 flex items-center gap-1" data-testid={`bank-iban-invalid-${idx}`}>
                            <AlertTriangle size={12} /> {ibs.warning}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        <Button variant="outline" onClick={add} data-testid="bank-add-btn">
          <Plus size={14} className="mr-1.5" /> Add another account
        </Button>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={() => onSubmit({ accounts })}
          disabled={busy || !accounts.some((a) => a.bank_name && a.iban)}
          data-testid="banks-save-btn"
          className="bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          Save & continue <ChevronRight size={14} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}
